import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeleteConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  category = null,
  categories = []
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [dependentCategories, setDependentCategories] = useState([]);
  const [deleteAction, setDeleteAction] = useState('cancel'); // 'cancel', 'delete-only', 'delete-with-children'

  useEffect(() => {
    if (isOpen && category) {
      // Find all dependent categories (children and descendants)
      const findDependents = (parentId, level = 1) => {
        const children = categories?.filter(cat => cat?.parentId === parentId);
        let dependents = [...children];
        
        children?.forEach(child => {
          dependents = [...dependents, ...findDependents(child?.id, level + 1)];
        });
        
        return dependents;
      };

      let dependents = findDependents(category?.id);
      setDependentCategories(dependents);
      setDeleteAction(dependents?.length > 0 ? 'cancel' : 'delete-only');
    }
  }, [isOpen, category, categories]);

  const handleConfirm = async () => {
    if (deleteAction === 'cancel') return;

    setIsDeleting(true);
    try {
      await onConfirm(category, deleteAction);
      onClose();
    } catch (error) {
      console.error('Error deleting category:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  if (!isOpen || !category) return null;

  const hasChildren = dependentCategories?.length > 0;
  const directChildren = dependentCategories?.filter(cat => cat?.parentId === category?.id);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-90"
        onClick={handleClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg border border-border shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-border">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Delete Category
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={handleClose}
              disabled={isDeleting}
            />
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Category Info */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Icon name="Package" size={20} className="text-muted-foreground" />
                <div>
                  <h3 className="font-medium text-foreground">{category?.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Level {category?.level} • {category?.parentCategory || 'Root Category'}
                  </p>
                </div>
              </div>
            </div>

            {/* Warning Message */}
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                Are you sure you want to delete the category <strong>"{category?.name}"</strong>?
              </p>

              {hasChildren && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertCircle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-warning">
                        This category has {dependentCategories?.length} dependent categories
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Deleting this category will affect its subcategories. Choose how to handle them:
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dependent Categories List */}
            {hasChildren && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">
                  Affected Categories ({dependentCategories?.length}):
                </h4>
                <div className="max-h-32 overflow-y-auto bg-muted/30 rounded-lg p-3 space-y-2">
                  {dependentCategories?.slice(0, 10)?.map(cat => (
                    <div key={cat?.id} className="flex items-center space-x-2 text-sm">
                      <Icon name="CornerDownRight" size={14} className="text-muted-foreground" />
                      <span className="text-foreground">{cat?.name}</span>
                      <span className="text-muted-foreground">(Level {cat?.level})</span>
                    </div>
                  ))}
                  {dependentCategories?.length > 10 && (
                    <p className="text-xs text-muted-foreground">
                      ... and {dependentCategories?.length - 10} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Delete Options */}
            {hasChildren && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">
                  Choose an action:
                </h4>
                <div className="space-y-2">
                  <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                    <input
                      type="radio"
                      name="deleteAction"
                      value="cancel"
                      checked={deleteAction === 'cancel'}
                      onChange={(e) => setDeleteAction(e?.target?.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">Cancel deletion</p>
                      <p className="text-xs text-muted-foreground">
                        Keep the category and all its subcategories
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                    <input
                      type="radio"
                      name="deleteAction"
                      value="delete-with-children"
                      checked={deleteAction === 'delete-with-children'}
                      onChange={(e) => setDeleteAction(e?.target?.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-destructive">
                        Delete category and all subcategories
                      </p>
                      <p className="text-xs text-muted-foreground">
                        This will permanently delete {dependentCategories?.length + 1} categories
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                    <input
                      type="radio"
                      name="deleteAction"
                      value="move-children"
                      checked={deleteAction === 'move-children'}
                      onChange={(e) => setDeleteAction(e?.target?.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Delete category, move children to parent
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Direct children will be moved to "{category?.parentCategory || 'Root Level'}"
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              loading={isDeleting}
              disabled={deleteAction === 'cancel'}
              iconName="Trash2"
              iconPosition="left"
            >
              {deleteAction === 'delete-with-children' 
                ? `Delete All (${dependentCategories?.length + 1})`
                : deleteAction === 'move-children' ?'Delete & Move Children' :'Delete Category'
              }
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteConfirmModal;